import { Colors, PrimaryButton, Text, WebLink } from 'library';
import React, { useState } from 'react';

import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import CreditCardIcon from 'assets/icons/tandempay/credit-card-circle.svg';
import client from 'shared/ApiClient';
import { useUserContext } from 'UserContext';
import { track } from 'shared/Tracking';
import ErrorText from '../../../ErrorText';
import PageActionsFooter from '../../PageActionsFooter';
import ModalPage from '../../ModalPage';
import PageContent from '../../PageContent';
import BillingAddressSection from './BillingAddressSection/BillingAddressSection';
import { Grid } from '@mui/material';
import PaymentPartners from 'parent-portal/payments/TandemPayInfo/PaymentPartners';
import { StripeCardElementChangeEvent, Token } from '@stripe/stripe-js';

interface ConnectCreditCardPageProps {
    goToSuccess: () => void;
    goBack: () => void;
    showHeaderAndFooter: boolean;
}

const stripeCardInputStyle = {
    base: {
        fontSize: '16px',
        fontWeight: 'normal',
        color: Colors.mediumGrey,
        fontFamily: 'Outfit',
        letterSpacing: 'normal',
        '::placeholder': {
            color: '#aab7c4',
        },
        width: '100%',
    },
    invalid: {
        color: Colors.red,
    },
};

export default function ConnectCreditCardPage({
    goToSuccess,
    goBack,
    showHeaderAndFooter,
}: ConnectCreditCardPageProps) {
    const stripe = useStripe();
    const elements = useElements();
    const { user } = useUserContext();

    const [errorMessage, setErrorMessage] = useState<string>();
    const [loading, setLoading] = useState(false);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [address, setAddress] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('Ohio');
    const [zip, setZip] = useState('');
    const [country, setCountry] = useState('US');

    function onCardDataChanged({ error }: StripeCardElementChangeEvent) {
        setErrorMessage(error?.message);
    }

    async function submit() {
        setLoading(true);
        const cardElement = elements?.getElement(CardElement);
        if (!cardElement) {
            return;
        }
        const tokenResponse = await stripe?.createToken(cardElement);

        if (tokenResponse?.error) {
            setErrorMessage(tokenResponse.error.message);
        } else {
            try {
                if (tokenResponse?.token) {
                    await updateAccount(tokenResponse.token);
                    goToSuccess();
                }
            } catch {
                setErrorMessage('There was an error adding your payment method');
            }
        }
        setLoading(false);
    }

    async function updateAccount(token: Token) {
        const response = await client('get-account/', {
            method: 'POST',
            body: {
                stripe_token: token.id,
                id: user?.id,
                stripe_card_id: token.card!.id,
                first_name: firstName,
                last_name: lastName,
                address: address,
                address_2: address2,
                city: city,
                state: state,
                postal_code: zip,
                country: country,
            },
        });
        if (response.Error != null) {
            alert(response.Error);
            track('Credit Card Setup', { status: 'failed', error: response.Error });
            client('payment/api/setup-events/', { body: { data: response, processor: 'Stripe' } });
        } else {
            track('Credit Card Setup', { status: 'success' });
        }
    }
    const BillingAddressFilledOut =
        lastName !== '' && address !== '' && city !== '' && state !== '' && zip !== '' && country !== '';

    return (
        <ModalPage
            iconSource={<CreditCardIcon />}
            headerText="Connect Credit Card"
            subtitleText={
                <>
                    Tandem uses{' '}
                    <WebLink newTab={true} url="https://stripe.com/" style={{ display: 'inline' }}>
                        Stripe
                    </WebLink>{' '}
                    to complete all credit card processing. Stripe incurs a $0.30 + 2.9% additional fee for handling
                    each credit card transaction.
                </>
            }
            showHeader={showHeaderAndFooter}
        >
            <PageContent>
                <Grid container style={{ padding: '0px 40px' }}>
                    <Grid container xs={12} md={10}>
                        <Grid container item style={{ paddingTop: 40 }}>
                            <Grid container item>
                                <Text variant="body1">Card Details</Text>
                            </Grid>
                            <Grid container item style={{ paddingTop: 10 }}>
                                <div
                                    style={{
                                        borderBottom: `1px solid ${Colors.mediumGrey}`,
                                        width: '100%',
                                    }}
                                >
                                    <CardElement
                                        options={{ style: stripeCardInputStyle }}
                                        onChange={onCardDataChanged}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid container item style={{ paddingTop: 50 }}>
                                <BillingAddressSection
                                    firstName={firstName}
                                    setFirstName={setFirstName}
                                    lastName={lastName}
                                    setLastName={setLastName}
                                    address={address}
                                    setAddress={setAddress}
                                    address2={address2}
                                    setAddress2={setAddress2}
                                    city={city}
                                    setCity={setCity}
                                    state={state}
                                    setState={setState}
                                    zip={zip}
                                    setZip={setZip}
                                    country={country}
                                    setCountry={setCountry}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {!showHeaderAndFooter && (
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                            marginTop={4}
                        >
                            <PaymentPartners />
                            <PrimaryButton
                                onClick={submit}
                                buttonStyle={{ width: undefined, height: 40 }}
                                disabled={!BillingAddressFilledOut}
                            >
                                Save
                            </PrimaryButton>
                        </Grid>
                    )}
                    <Grid container style={{ paddingTop: 30 }}>
                        {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
                    </Grid>
                </Grid>
            </PageContent>
            {showHeaderAndFooter && (
                <PageActionsFooter
                    save={submit}
                    goBack={goBack}
                    loading={loading}
                    disabled={!BillingAddressFilledOut}
                />
            )}
        </ModalPage>
    );
}
